import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Onboarding component that highlights elements and guides users through the app
 * 
 * @param {Object} props
 * @param {Array} props.steps - Array of step objects with { target, title, description, position }
 * @param {string} props.onboardingKey - Key to track completion in user profile ('dashboard', 'canvas', 'canvas_sidebar')
 * @param {boolean} props.hasCompleted - Whether user has already completed this onboarding
 * @param {function} props.onComplete - Callback when onboarding is completed
 */
export default function Onboarding({ steps, onboardingKey, hasCompleted, onComplete }) {
  const { refreshUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [targetRect, setTargetRect] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);

  // Check if user has already completed onboarding and show with delay
  useEffect(() => {
    if (hasCompleted) {
      // Already completed, don't show
      if (onComplete) onComplete();
      return;
    }
    
    if (steps.length > 0) {
      // Delay showing onboarding to let the page render
      const timer = setTimeout(() => {
        setIsVisible(true);
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          updateTargetPosition();
        });
      }, 1200);
      return () => clearTimeout(timer);
    }
  }, [hasCompleted, steps.length, onComplete]);

  // Update target element position
  const updateTargetPosition = useCallback(() => {
    if (!steps[currentStep]) return;
    
    const targetEl = document.querySelector(steps[currentStep].target);
    if (targetEl) {
      const rect = targetEl.getBoundingClientRect();
      setTargetRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
    } else {
      // Element not found yet, try again after a short delay
      setTimeout(() => {
        const el = document.querySelector(steps[currentStep].target);
        if (el) {
          const rect = el.getBoundingClientRect();
          setTargetRect({
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          });
        }
      }, 500);
    }
  }, [currentStep, steps]);

  // Update position on step change and window resize
  useEffect(() => {
    if (isVisible) {
      setIsAnimating(true);
      updateTargetPosition();
      const timer = setTimeout(() => setIsAnimating(false), 300);
      return () => clearTimeout(timer);
    }
  }, [currentStep, isVisible, updateTargetPosition]);

  useEffect(() => {
    window.addEventListener('resize', updateTargetPosition);
    window.addEventListener('scroll', updateTargetPosition);
    return () => {
      window.removeEventListener('resize', updateTargetPosition);
      window.removeEventListener('scroll', updateTargetPosition);
    };
  }, [updateTargetPosition]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      completeOnboarding();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    completeOnboarding();
  };

  const completeOnboarding = async () => {
    // Mark onboarding as complete in the backend
    try {
      await api.post('/auth/onboarding', { key: onboardingKey, completed: true });
      // Refresh user data to update onboarding state
      await refreshUser();
    } catch (err) {
      console.error('Failed to save onboarding status:', err);
    }
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  if (!isVisible || !steps[currentStep]) return null;
  
  // If targetRect is null, try to update it
  if (!targetRect) {
    // Return a loading state or try again
    return null;
  }

  const step = steps[currentStep];
  const padding = 8;

  // Calculate tooltip position based on step.position
  const getTooltipStyle = () => {
    const tooltipWidth = 320;
    const tooltipHeight = 200;
    const gap = 16;

    let top, left;
    const position = step.position || 'bottom';

    switch (position) {
      case 'top':
        top = targetRect.top - tooltipHeight - gap;
        left = targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2);
        break;
      case 'bottom':
        top = targetRect.top + targetRect.height + gap;
        left = targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2);
        break;
      case 'left':
        top = targetRect.top + (targetRect.height / 2) - (tooltipHeight / 2);
        left = targetRect.left - tooltipWidth - gap;
        break;
      case 'right':
        top = targetRect.top + (targetRect.height / 2) - (tooltipHeight / 2);
        left = targetRect.left + targetRect.width + gap;
        break;
      default:
        top = targetRect.top + targetRect.height + gap;
        left = targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2);
    }

    // Keep tooltip within viewport
    if (left < 16) left = 16;
    if (left + tooltipWidth > window.innerWidth - 16) {
      left = window.innerWidth - tooltipWidth - 16;
    }
    if (top < 16) top = targetRect.top + targetRect.height + gap;
    if (top + tooltipHeight > window.innerHeight - 16) {
      top = targetRect.top - tooltipHeight - gap;
    }

    return { top, left, width: tooltipWidth };
  };

  const tooltipStyle = getTooltipStyle();

  return (
    <div className="fixed inset-0 z-[9999]" data-testid="onboarding-overlay">
      {/* Backdrop with cutout */}
      <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
        <defs>
          <mask id="spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              x={targetRect.left - padding}
              y={targetRect.top - padding}
              width={targetRect.width + padding * 2}
              height={targetRect.height + padding * 2}
              rx="8"
              fill="black"
              className={isAnimating ? 'transition-all duration-300' : ''}
            />
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.75)"
          mask="url(#spotlight-mask)"
          style={{ pointerEvents: 'auto' }}
          onClick={handleSkip}
        />
      </svg>

      {/* Highlight border */}
      <div
        className={`absolute border-2 border-cobalt rounded-lg pointer-events-none ${isAnimating ? 'transition-all duration-300' : ''}`}
        style={{
          top: targetRect.top - padding,
          left: targetRect.left - padding,
          width: targetRect.width + padding * 2,
          height: targetRect.height + padding * 2,
          boxShadow: '0 0 0 4px rgba(0, 71, 171, 0.3), 0 0 20px rgba(0, 71, 171, 0.4)',
        }}
      />

      {/* Pulse effect */}
      <div
        className={`absolute rounded-lg pointer-events-none animate-pulse ${isAnimating ? 'transition-all duration-300' : ''}`}
        style={{
          top: targetRect.top - padding - 4,
          left: targetRect.left - padding - 4,
          width: targetRect.width + padding * 2 + 8,
          height: targetRect.height + padding * 2 + 8,
          border: '2px solid rgba(0, 71, 171, 0.5)',
        }}
      />

      {/* Tooltip */}
      <div
        className={`absolute bg-card border border-border rounded-xl shadow-2xl p-5 ${isAnimating ? 'transition-all duration-300 opacity-0' : 'opacity-100'}`}
        style={{
          top: tooltipStyle.top,
          left: tooltipStyle.left,
          width: tooltipStyle.width,
          animation: isAnimating ? 'none' : 'fadeIn 0.3s ease-out forwards',
        }}
        data-testid="onboarding-tooltip"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            Step {currentStep + 1} of {steps.length}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSkip}
            className="h-6 w-6 p-0 hover:bg-muted"
            data-testid="onboarding-skip"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Content */}
        <h3 className="font-display text-lg font-semibold text-foreground mb-2">
          {step.title}
        </h3>
        <p className="font-body text-sm text-muted-foreground leading-relaxed mb-4">
          {step.description}
        </p>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-1.5 mb-4">
          {steps.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentStep
                  ? 'bg-cobalt w-6'
                  : index < currentStep
                  ? 'bg-cobalt/50'
                  : 'bg-muted'
              }`}
            />
          ))}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className="font-body text-sm"
            data-testid="onboarding-prev"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Previous
          </Button>
          <Button
            size="sm"
            onClick={handleNext}
            className="bg-cobalt hover:bg-cobalt/90 text-white font-body text-sm"
            data-testid="onboarding-next"
          >
            {currentStep === steps.length - 1 ? 'Get Started' : 'Next'}
            {currentStep < steps.length - 1 && <ChevronRight className="w-4 h-4 ml-1" />}
          </Button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
